import { useEffect, type RefObject } from "react";
import { reactions } from "../avatar/reaction-bus";
import { HOLD_AFTER_MS, gestureForTap } from "./touch-zones";

/**
 * Makes Orla answer a finger on the picture: a tap plays the gesture for where it landed, and
 * a press that stays down makes her run until it lifts. Touch and pen only, since a mouse
 * already has hover. A touch that lands on a link or a button is left to that control.
 */
export function useTouchReactions(picture: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = picture.current;
    if (!element) return;
    let holdTimer: number | undefined;
    let running = false;
    let tap: { x: number; y: number } | null = null;

    const release = () => {
      window.clearTimeout(holdTimer);
      if (running) reactions.send({ kind: "release" });
      running = false;
    };

    const onDown = (event: PointerEvent) => {
      if (event.pointerType === "mouse") return;
      if (event.target instanceof Element && event.target.closest("a, button, dialog")) return;
      const box = element.getBoundingClientRect();
      tap = {
        x: (event.clientX - box.left) / box.width,
        y: (event.clientY - box.top) / box.height,
      };
      holdTimer = window.setTimeout(() => {
        running = true;
        tap = null;
        reactions.send({ kind: "hold", name: "Run" });
      }, HOLD_AFTER_MS);
    };

    const onUp = () => {
      const landed = tap;
      tap = null;
      const wasRunning = running;
      release();
      if (landed && !wasRunning) {
        reactions.send({ kind: "gesture", name: gestureForTap(landed.x, landed.y) });
      }
    };

    const onCancel = () => {
      tap = null;
      release();
    };

    element.addEventListener("pointerdown", onDown);
    element.addEventListener("pointerup", onUp);
    element.addEventListener("pointercancel", onCancel);
    element.addEventListener("pointerleave", onCancel);
    return () => {
      release();
      element.removeEventListener("pointerdown", onDown);
      element.removeEventListener("pointerup", onUp);
      element.removeEventListener("pointercancel", onCancel);
      element.removeEventListener("pointerleave", onCancel);
    };
  }, [picture]);
}
